// Site-wide constants for CRS Energy.

import napit from "@/assets/accreditations/napit.png.asset.json";
import iaa from "@/assets/accreditations/iaa.png.asset.json";
import mcsCertified from "@/assets/accreditations/mcs-certified.png.asset.json";
import hiesLogo from "@/assets/accreditations/hies-logo.jpg.asset.json";
import kiwa from "@/assets/accreditations/kiwa.webp";
import employeeOwned from "@/assets/accreditations/employee-owned.webp";
import gasSafe from "@/assets/accreditations/gas-safe.webp";
import trustmark from "@/assets/accreditations/trustmark.webp";
import refcom from "@/assets/accreditations/refcom.png.asset.json";
import chas from "@/assets/accreditations/chas.png.asset.json";
import constructionlineGold from "@/assets/accreditations/constructionline-gold.png.asset.json";

import kiwaInstaller from "@/assets/accreditations/kiwa-installer.webp";
import bfc from "@/assets/accreditations/bfc.webp";

import awardWalesWinner from "@/assets/awards/wales-vulnerable-winner.webp";
import awardWales2025Commended from "@/assets/awards/wales-2025-insulation-commended.png.asset.json";
import awardNational2025Shortlisted from "@/assets/awards/national-2025-shortlisted.png.asset.json";

export const SITE = {
  name: "CRS Energy",
  legalName: "Complete Remedial Solutions (CRS) Ltd",
  url: "https://crsenergy.org",
  phone: "0800 009 2992",
  phoneTel: "tel:+448000092992",
  phoneSecondary: "01656 253194",
  phoneSecondaryTel: "tel:+441656253194",
  email: "hello@crsenergy.org",
  emailHref: "mailto:hello@crsenergy.org",
  address: {
    line1: "Unit 14, Queens Court",
    line2: "Bridgend Industrial Estate",
    city: "Bridgend",
    postcode: "CF31 3TQ",
    country: "United Kingdom",
  },
  geo: {
    latitude: 51.5081,
    longitude: -3.5779,
    region: "GB-BGE",
    placename: "Bridgend, Wales",
  },
  companyNumber: "11582758",
  social: {
    facebook: "https://www.facebook.com/completeremedialsolutions/?locale=en_GB",
    linkedin: "https://www.linkedin.com/company/complete-remediation-solutions",
  },
};

/** Build an absolute URL for the canonical site domain. */
export const absoluteUrl = (path: string) =>
  `${SITE.url}${path.startsWith("/") ? path : `/${path}`}`;

export const COVERAGE_AREAS = [
  "Bridgend",
  "Cardiff",
  "Swansea",
  "Newport",
  "Carmarthen",
  "Pembroke",
  "Powys",
  "Hereford",
  "Bristol",
  "Bath",
  "Swindon",
  "Wiltshire",
  "Taunton",
  "Exeter",
  "Plymouth",
  "Truro",
];

export const ACCREDITATION_LOGOS = [
  { src: napit.url, alt: "NAPIT Registered" },
  { src: mcsCertified.url, alt: "MCS Certified" },
  { src: iaa.url, alt: "Installation Assurance Authority" },
  { src: kiwa, alt: "Kiwa" },
  { src: employeeOwned, alt: "Proudly Employee Owned" },
  { src: gasSafe, alt: "Gas Safe Register" },
  { src: trustmark, alt: "TrustMark Government Endorsed Quality" },
  { src: hiesLogo.url, alt: "HIES Home Insulation and Energy Systems Quality Assured Contractors Scheme" },
  { src: kiwaInstaller, alt: "Kiwa Assessed Approved Installer" },
  { src: bfc, alt: "BFC Approved Competent Persons" },
  { src: refcom.url, alt: "REFCOM F-Gas Certified" },
  { src: chas.url, alt: "CHAS Accredited Contractor" },
  { src: constructionlineGold.url, alt: "Constructionline Gold Member" },
];

export const ACCREDITATIONS = [
  {
    slug: "stroma",
    name: "Stroma Certified",
    body: "Independent third-party certification of installer competence and on-site compliance with PAS 2030 retrofit standards.",
  },
  {
    slug: "trustmark",
    name: "TrustMark",
    body: "The government endorsed quality scheme for tradespeople. Mandatory for any work carried out under government grant funding.",
  },
  {
    slug: "hies",
    name: "HIES",
    body: "Home Insulation and Energy Systems Quality Assured Contractors Scheme. Consumer protection and installer quality for renewable and energy-efficiency installations.",
  },
  {
    slug: "oscar",
    name: "OSCAR",
    body: "Onsite Compliance Assurance Register: independent verification of installation quality and process discipline.",
  },
  {
    slug: "gas-safe",
    name: "Gas Safe Registered",
    body: "Legally required for any gas appliance work in the UK. All CRS heating engineers are registered and ID-carded.",
  },
  {
    slug: "constructionline",
    name: "Constructionline",
    body: "Pre-qualified supplier accreditation used by major buyers across UK construction. Verified financial, H&S and competence records.",
  },
  {
    slug: "chas",
    name: "CHAS",
    body: "Contractors Health and Safety Assessment Scheme: independently verified health and safety competence.",
  },
  {
    slug: "refcom",
    name: "REFCOM F-Gas Certified",
    body: "F-Gas Company Certification under the F-Gas Regulations. Demonstrates competence for the installation, servicing and maintenance of stationary refrigeration, air conditioning and heat pump equipment containing F-Gas refrigerants.",
  },
];

import qbaBadge from "@/assets/awards/qba-2025-insulation-vale-of-glamorgan.svg.asset.json";

export interface Award {
  image: string;
  title: string;
  result: string;
  category: string;
  href?: string;
}

export const AWARDS: Award[] = [
  {
    image: qbaBadge.url,
    title: "Quality Business Awards 2025",
    result: "Winner",
    category: "Best Insulation Contractor, Vale of Glamorgan",
    href: "https://qualitybusinessawards.co.uk/2025/the-best-insulation-contractor-in-vale-of-glamorgan/complete-remedial-solutions-ltd/",
  },
  {
    image: awardWalesWinner,
    title: "Wales Cymru Energy Efficiency Awards 2018",
    result: "Winner",
    category: "Vulnerable Customer Support",
  },
  {
    image: awardWales2025Commended.url,
    title: "Wales Energy Efficiency Awards 2025",
    result: "Commended",
    category: "Insulation & Fabric Installer and Contractor",
  },
  {
    image: awardNational2025Shortlisted.url,
    title: "National Energy Efficiency Awards 2025",
    result: "Shortlisted",
    category: "Insulation & Fabric Installer and Contractor",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "We had cavity wall insulation done which has made a big difference to our house. Our rooms were cold and we had some mould in corners of the rooms. Since having it done there hasn't been any. House warms up quicker and stays warmer longer. So glad we had it done and it was quick and easy. CRS were lovely and I would recommend them to anyone.",
    name: "Liz S",
    date: "February 2025",
    source: "google" as const,
  },
  {
    quote:
      "As a homeowner you often have people in to fix things and I can honestly say this company is exceptional. From the initial approach through to completion everything was carried out in a friendly, respectful and professional way, from the initial survey and admin office through to the fitters who carried out the work. People turned up when they said they would and did the work with skill and efficiency.",
    name: "Richard Worsnop",
    date: "July 2026",
    source: "trustpilot" as const,
  },
  {
    quote:
      "I cannot praise this company too highly. From first contact to completion of the work the whole experience was perfect. People turned up when they were supposed to and were all friendly, good-humoured and respectful, and they knew what they were doing. All three teams that carried out the work were well coordinated and efficient. And they all cleared up after them!",
    name: "Richard W",
    date: "September 2025",
    source: "google" as const,
  },
];
